import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';

const DOCUMENT_TYPES = [
  { value: 'discharge_summary', label: 'Discharge Summary' },
  { value: 'final_bill', label: 'Final Bill' },
  { value: 'investigation_reports', label: 'Investigation Reports' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'policy_copy', label: 'Policy Copy' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'pre_auth_letter', label: 'Pre-Auth Letter' },
  { value: 'claim_form', label: 'Claim Form' },
  { value: 'medical_certificate', label: 'Medical Certificate' },
  { value: 'surgery_notes', label: 'Surgery Notes' },
  { value: 'other', label: 'Other' },
];

const DENIAL_CATEGORIES = [
  { value: 'documentation_incomplete', label: 'Documentation Incomplete' },
  { value: 'policy_not_active', label: 'Policy Not Active' },
  { value: 'pre_existing_condition', label: 'Pre-existing Condition' },
  { value: 'non_covered_service', label: 'Non-covered Service' },
  { value: 'exceeds_policy_limit', label: 'Exceeds Policy Limit' },
  { value: 'late_submission', label: 'Late Submission' },
  { value: 'incorrect_coding', label: 'Incorrect Coding' },
  { value: 'authorization_not_obtained', label: 'Authorization Not Obtained' },
  { value: 'other', label: 'Other' },
];

export const EditDocumentDialog = ({ open, onClose, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-['Outfit']">Edit Document</DialogTitle><DialogDescription>Update document type or notes</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div><Label>Document Type</Label>
          <Select value={form.document_type} onValueChange={v => setForm(f => ({...f, document_type: v}))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{DOCUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Notes" /></div>
        <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" onClick={onSave}>Save</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const EditPreauthDialog = ({ open, onClose, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-['Outfit']">Edit Pre-Auth</DialogTitle><DialogDescription>Correct pre-auth details</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Requested Amount</Label><Input type="number" value={form.requested_amount} onChange={e => setForm(f => ({...f, requested_amount: e.target.value}))} /></div>
          <div><Label>Expected LOS</Label><Input type="number" value={form.expected_los} onChange={e => setForm(f => ({...f, expected_los: e.target.value}))} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Approved Amount</Label><Input type="number" value={form.approved_amount} onChange={e => setForm(f => ({...f, approved_amount: e.target.value}))} /></div>
          <div><Label>Approved LOS</Label><Input type="number" value={form.approved_los} onChange={e => setForm(f => ({...f, approved_los: e.target.value}))} /></div>
        </div>
        <div><Label>Diagnosis</Label><Input value={form.diagnosis} onChange={e => setForm(f => ({...f, diagnosis: e.target.value}))} /></div>
        <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} /></div>
        <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" onClick={onSave}>Save</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const EditDenialDialog = ({ open, onClose, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-['Outfit']">Edit Denial</DialogTitle><DialogDescription>Correct denial details</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div><Label>Category</Label>
          <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{DENIAL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Reason</Label><Input value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} /></div>
        <div><Label>Deduction Amount</Label><Input type="number" value={form.deduction_amount} onChange={e => setForm(f => ({...f, deduction_amount: e.target.value}))} /></div>
        <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} /></div>
        <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" onClick={onSave}>Save</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const EditFollowupDialog = ({ open, onClose, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-['Outfit']">Edit Follow-up</DialogTitle><DialogDescription>Correct follow-up entry</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div><Label>Type</Label>
          <Select value={form.followup_type} onValueChange={v => setForm(f => ({...f, followup_type: v}))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="call">Call</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="letter">Letter</SelectItem>
              <SelectItem value="visit">Visit</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={e => setForm(f => ({...f, contact_person: e.target.value}))} /></div>
        <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} /></div>
        <div><Label>Next Action</Label><Input value={form.next_action} onChange={e => setForm(f => ({...f, next_action: e.target.value}))} /></div>
        <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" onClick={onSave}>Save</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const EditPaymentDialog = ({ open, onClose, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-sm">
      <DialogHeader><DialogTitle className="font-['Outfit']">Edit Payment</DialogTitle><DialogDescription>Correct payment entry</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} /></div>
          <div><Label>Payment Date</Label><Input type="date" value={form.payment_date} onChange={e => setForm(f => ({...f, payment_date: e.target.value}))} /></div>
        </div>
        <div><Label>Mode</Label>
          <Select value={form.payment_mode} onValueChange={v => setForm(f => ({...f, payment_mode: v}))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="neft">NEFT</SelectItem>
              <SelectItem value="rtgs">RTGS</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="dd">DD</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label>Reference Number</Label><Input value={form.reference_number} onChange={e => setForm(f => ({...f, reference_number: e.target.value}))} /></div>
        <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} /></div>
        <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" onClick={onSave}>Save</Button>
      </div>
    </DialogContent>
  </Dialog>
);
